// client/src/coms/components/comslayout_parts/TabRouter.js
import React, { useEffect } from 'react';
import ChatView from '../../views/ChatView';
import VoiceView from '../../views/VoiceView';
import MapsView from '../../views/MapsView';
import SocialView from '../../views/SocialView';
import WatchTogetherView from '../../views/WatchTogetherView';
import SettingsView from '../../views/SettingsView';
import MarketplaceView from '../../views/MarketplaceView';
import CanvasView from '../../views/canvas_parts/CanvasView';
import StudioView from '../../views/studio_parts/StudioView';
import VideoGridPanel from '../VideoGridPanel';
import ServerAdmin from '../../serveradmin/ServerAdmin';
import ServerEventsView from '../../serveradmin/ServerEventsView';
import CommunityFrame from '../../serveradmin/CommunityFrame';
import LibraryView from '../../organisation/LibraryView';
import SearchView from '../../organisation/SearchView';
import BookmarksView from '../../organisation/BookmarksView';

const LS_LAST_OPEN_MAP = 'vox:lastOpenMap';

export default function TabRouter({
  tab,
  server,
  channel,
  user,
  voiceSession,
  chatNameById,
  voiceRoomObj,
  voiceShowVideo,
  messageSearch,
  channelReady,
  onOpenTab,
  apiAvailable = true,
  socialSection = 'dms',
  setSocialSection,
  selectedSocialThreadId = '',
  setSelectedSocialThreadId,
  onMessageMetaChanged,
  onMessageRemoved,
  // Conversation tabs: which conversation this is (its scroll/reply memory) and whether it may take focus.
  conversationKey = '',
  chatAutoFocus = true,
  onCustomiseLayout,
  // Organisation areas: deep-link context ({ area, tab, source, file_id, resource_id, q, scope, ... }).
  orgContext = null,
}) {
  const serverKey =
    server && server.id != null
      ? String(server.id)
      : server && server.public_id != null
        ? String(server.public_id)
        : server && server.server_key != null
          ? String(server.server_key)
          : '';

  useEffect(() => {
    if (tab !== 'maps') return;

    try {
      const rememberedMap = String(window.localStorage.getItem(LS_LAST_OPEN_MAP) || '').trim();
      if (!rememberedMap) return;
      if (!/\/maps?(\/|$)/i.test(rememberedMap)) return;
      if (`${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}` === rememberedMap) return;
      window.location.assign(rememberedMap);
    } catch {}
  }, [tab]);

  if (tab === 'maps') {
    return (
      <MapsView
        server={server}
        serverKey={serverKey}
        user={user}
      />
    );
  }

  if (tab === 'social') {
    return (
      <SocialView
        server={server}
        user={user}
        socialSection={socialSection}
        onSelectSection={setSocialSection}
        selectedThreadId={selectedSocialThreadId}
        onSelectThreadId={setSelectedSocialThreadId}
      />
    );
  }

  if (tab === 'watch') {
    return (
      <WatchTogetherView
        server={server}
        user={user}
        voiceRoom={voiceRoomObj}
        voiceSession={voiceSession}
      />
    );
  }

  if (tab === 'canvas') {
    return (
      <CanvasView
        server={server}
        user={user}
        voiceRoom={voiceRoomObj}
        voiceSession={voiceSession}
      />
    );
  }

  // ComsLayout hosts the Games view itself so a running game survives switching to other areas.
  if (tab === 'games') return null;

  if (tab === 'studio') {
    return (
      <StudioView
        server={server}
        user={user}
        voiceRoom={voiceRoomObj}
        voiceSession={voiceSession}
      />
    );
  }

  if (tab === 'marketplace') {
    return (
      <MarketplaceView
        onOpenSettings={() => {
          try {
            window.__voxPendingSettingsTab = 'Stickers';
          } catch {}
          if (typeof onOpenTab === 'function') onOpenTab('settings');
        }}
      />
    );
  }

  if (tab === 'library') {
    return <LibraryView key={serverKey} server={server} initial={orgContext && orgContext.area === 'library' ? orgContext : null} />;
  }

  if (tab === 'search') {
    return <SearchView server={server} channel={channel} initial={orgContext && orgContext.area === 'search' ? orgContext : null} />;
  }

  if (tab === 'bookmarks') {
    return <BookmarksView />;
  }

  if (tab === 'settings') {
    return (
      <SettingsView
        user={user}
        serverKey={serverKey}
        serverName={server && server.name ? String(server.name) : ''}
        serverChannels={server && Array.isArray(server.channels) ? server.channels : []}
        onCustomiseLayout={onCustomiseLayout}
        onClose={() => {
          if (typeof onOpenTab === 'function') onOpenTab('coms');
        }}
      />
    );
  }

  // Server Settings: sections follow the member's permissions. vox-open-server-admin { section }
  // can pick the section (window.__voxPendingAdminSection when the view is not open yet).
  if (tab === 'server-settings') {
    return (
      <ServerAdmin
        server={server}
        user={user}
        apiAvailable={apiAvailable}
        initialSection={typeof window !== 'undefined' ? window.__voxPendingAdminSection : undefined}
        onClose={() => {
          if (typeof window !== 'undefined') window.__voxPendingAdminSection = undefined;
          if (typeof onOpenTab === 'function') onOpenTab('coms');
        }}
      />
    );
  }

  if (tab === 'server-events') {
    return (
      <ServerEventsView
        server={server}
        onClose={() => typeof onOpenTab === 'function' && onOpenTab('coms')}
        onManage={() => {
          window.__voxPendingAdminSection = 'announcements';
          if (typeof onOpenTab === 'function') onOpenTab('server-settings');
        }}
      />
    );
  }

  // Onboarding / approval screen and private server notices around the server's views (also when no
  // channel is open: members waiting for approval cannot see any channel).
  const frame = (view) => (server && serverKey ? <CommunityFrame serverKey={serverKey}>{view}</CommunityFrame> : view);

  if (tab === 'coms' && server && !channelReady) {
    return frame(<div className="coms-view" />);
  }

  if (channel && channel.kind === 'voice') {
    if (voiceShowVideo) {
      return frame(
        <div className="coms-view">
          <div className="coms-voice-card">
            <VideoGridPanel room={voiceRoomObj} server={server} serverKey={serverKey} />
          </div>
        </div>
      );
    }

    return frame(
      <VoiceView
        server={server}
        channel={channel}
        user={user}
        voiceSession={voiceSession}
        voiceRoom={voiceRoomObj}
        voiceShowVideo={voiceShowVideo}
      />
    );
  }

  return frame(
    <ChatView
      server={server}
      channel={channel}
      user={user}
      chatNameById={chatNameById}
      messageSearch={messageSearch}
      onMessageMetaChanged={onMessageMetaChanged}
      onMessageRemoved={onMessageRemoved}
      memoryKey={conversationKey}
      autoFocus={chatAutoFocus}
    />
  );
}